import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Search, Star, Shield, TrendingUp, ArrowRight, CheckCircle, MessageSquare, FileText, Users, Award, Clock } from 'lucide-react';

const TradiesLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const categories = [
    { name: 'Plumbing', icon: '🔧', color: 'from-blue-500 to-blue-600' },
    { name: 'Electrical', icon: '⚡', color: 'from-yellow-500 to-yellow-600' },
    { name: 'Roofing', icon: '🏠', color: 'from-red-500 to-red-600' },
    { name: 'Carpentry', icon: '🪚', color: 'from-orange-500 to-orange-600' },
    { name: 'Painting', icon: '🎨', color: 'from-purple-500 to-purple-600' },
    { name: 'Landscaping', icon: '🌳', color: 'from-green-500 to-green-600' },
    { name: 'HVAC', icon: '❄️', color: 'from-cyan-500 to-cyan-600' },
    { name: 'Tiling', icon: '⬛', color: 'from-gray-500 to-gray-600' },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>

        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white drop-shadow-lg">
              Your future starts here
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-100 max-w-3xl mx-auto drop-shadow">
              Australia's trusted marketplace connecting you with verified tradies. Find quality services, get quotes, and hire with confidence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <button
                onClick={() => navigate('/tradies/browse')}
                className="px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all transform hover:scale-105 shadow-xl flex items-center justify-center gap-2 text-lg"
              >
                <Search className="w-6 h-6" />
                Find a Tradie
              </button>
              
              {user ? (
                user.has_tradies_profile ? (
                  <button
                    onClick={() => navigate('/tradies/dashboard')}
                    className="px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all transform hover:scale-105 shadow-xl text-lg border-2 border-white/20"
                  >
                    Go to Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/tradies/onboarding')}
                    className="px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all transform hover:scale-105 shadow-xl text-lg border-2 border-white/20"
                  >
                    Join as a Tradie
                  </button>
                )
              ) : (
                <button
                  onClick={() => navigate('/register')}
                  className="px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-all transform hover:scale-105 shadow-xl text-lg border-2 border-white/20"
                >
                  Join as a Tradie
                </button>
              )}
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-white">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Users className="w-5 h-5" />
                <span className="font-medium">100+ Verified Tradies</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <Star className="w-5 h-5 fill-current" />
                <span className="font-medium">4.8/5 Average Rating</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full backdrop-blur-sm">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">1000+ Jobs Completed</span>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Separator */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" className="fill-background"/>
          </svg>
        </div>
      </section>

      {/* How GoTradies Works */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">How GOFELLA Works</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Three simple steps to find and hire quality tradies
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                1
              </div>
              <div className="mb-4">
                <Search className="w-12 h-12 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Search & Browse</h3>
              <p className="text-muted-foreground">
                Find tradies by category, location, and ratings. Browse verified profiles with real reviews.
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                2
              </div>
              <div className="mb-4">
                <MessageSquare className="w-12 h-12 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Chat & Quote</h3>
              <p className="text-muted-foreground">
                Message tradies directly and request detailed quotes. Compare prices and choose the best fit.
              </p>
            </div>

            <div className="text-center p-6 rounded-2xl bg-card hover:bg-muted/50 transition-colors border border-border">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-6 shadow-lg">
                3
              </div>
              <div className="mb-4">
                <CheckCircle className="w-12 h-12 text-blue-600 mx-auto" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Done & Review</h3>
              <p className="text-muted-foreground">
                Get the job done professionally and leave a review to help others in the community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Categories */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-foreground">Popular Categories</h2>
            <p className="text-xl text-muted-foreground">Browse tradies by service category</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {categories.map((category, index) => (
              <button
                key={index}
                onClick={() => navigate(`/tradies/browse?category=${category.name}`)}
                className="group relative bg-card rounded-xl p-6 shadow-md hover:shadow-xl transition-all duration-300 border-2 border-border hover:border-blue-300 dark:hover:border-blue-600 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-5 transition-opacity`}></div>
                <div className="relative z-10">
                  <div className="text-4xl mb-3">{category.icon}</div>
                  <h3 className="font-semibold text-foreground group-hover:text-blue-600 transition-colors">
                    {category.name}
                  </h3>
                </div>
              </button>
            ))}
          </div>

          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/tradies/browse')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors shadow-lg"
            >
              View All Categories
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose GoTradies */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-foreground">Why Choose GoTradies?</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              The smart way to find and hire quality tradespeople
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-6 bg-card rounded-2xl border border-border">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Verified Tradies</h3>
              <p className="text-muted-foreground">
                All tradies are verified with ABN, licenses, and insurance checks for your peace of mind.
              </p>
            </div>
            
            <div className="text-center p-6 bg-card rounded-2xl border border-border">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Real Reviews</h3>
              <p className="text-muted-foreground">
                Read genuine reviews from verified customers to make informed decisions about who to hire.
              </p>
            </div>
            
            <div className="text-center p-6 bg-card rounded-2xl border border-border">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Grow Your Business</h3>
              <p className="text-muted-foreground">
                Tradies can showcase work, get more leads, and manage quotes all in one convenient place.
              </p>
            </div>

            <div className="text-center p-6 bg-card rounded-2xl border border-border">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Direct Messaging</h3>
              <p className="text-muted-foreground">
                Chat directly with tradies, ask questions, and get quotes without any middleman.
              </p>
            </div>

            <div className="text-center p-6 bg-card rounded-2xl border border-border">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Quote Management</h3>
              <p className="text-muted-foreground">
                Request, compare, and manage quotes easily. Get detailed breakdowns and professional PDFs.
              </p>
            </div>

            <div className="text-center p-6 bg-card rounded-2xl border border-border">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-foreground">Fast Response</h3>
              <p className="text-muted-foreground">
                Get responses quickly from available tradies. See who's "free now" and ready to work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white drop-shadow-lg">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-blue-100 mb-8 drop-shadow">
              Whether you're looking for a tradie or you are a tradie, GoTradies has you covered
            </p>
            
            <button
              onClick={() => navigate('/tradies/browse')}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 font-semibold rounded-xl hover:bg-blue-50 transition-all shadow-xl text-lg"
            >
              Browse Tradies Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TradiesLanding;
